import { TextInput } from 'dbi-pearl-ui-kit';
import {
    FormRow,
    PearlError,
    VendorAccountSetupStepFooter,
    VendorAccountSetupStepLayout,
    WebstoreAddressService,
} from 'framework';
import { formFieldNames } from 'framework/lib/WebstoreAddressService/WebstoreAddressService';
import { equifaxService } from 'lib/apis/equifax';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useFormContext } from 'react-hook-form';
import { useStore } from 'stores/Store';
import { AddressAutoComplete } from 'vendor/components/AddressAutoComplete/AddressAutoCompleteNew';
import {
    ClaimableWebstoresDialog,
    resolveCategoryName,
    resolveSubcategoryName,
} from 'vendor/components/ClaimableWebstoresDialog/ClaimableWebstoresDialog';
import { ClaimableWebstoresService } from 'lib/apis/ClaimableWebstoresService';
import { GeocodeService } from 'lib/apis/GeocodeService';
import { StorageService } from 'lib/StorageService';
import { PearlModalSizes, usePearlModal } from 'framework';
import PearlForm from 'vendor/components/forms/PearlForm';
import { VendorCategoryMultiSelect } from 'vendor/components/VendorCategoryMultiSelect';
import { VendorSubcategoryMultiSelect } from 'vendor/components/VendorSubcategoryMultiSelect';
import TooltipComponent from 'vendor/components/Utility/TooltipComponent/TooltipComponent';
import { SocialLinksSection } from 'vendor/components/Webstore/SocialLinksSection/SocialLinksSectionNew';
import { mergeSchemas } from 'vendor/components/Webstore/WebstoreHelpers/WebstoreHelpers';
import { useWebstoreSetupWizard } from 'vendor/components/WebstoreSetup/NewWebstoreSetupContext';
import { getSetupStepDisplayCounts } from 'shared/utils/pearlVendorSignupFlow';
import { vendorBusinessAddressFormSchema } from 'vendor/constants/schema/VendorBusinessAddressFormSchema';
import { vendorBusinessDetailsFormSchemaWrapper } from 'vendor/constants/schema/VendorBusinessDetailsFormSchemaNew';
import { useWebstoreContext } from 'vendor/contexts/WebstoreContext/WebstoreContext';
import { WebstoreService } from 'vendor/lib/apis/WebstoreService';
import { buildWebstoreSetupStepPageData, useTealium } from '../../../../Tealium';
import { useAnalytics } from 'shared/hooks/useAnalytics';
import { useVendorContext } from '../../../contexts/VendorContext';
import { NewVendorWebstoreSetupHeader } from '../NewVendorWebstoreSetupHeader';
import { VendorBusinessDetailsFormStyledNew } from './VendorBusinessDetailsFormNew.styled';
const formName = 'BusinessInformation';

const buildDefaultVendorSubCategoryIds = (webstore) => {
    const raw = webstore.VendorSubCategoryIds;
    const catIds = webstore.VendorCategoryId
        ? String(webstore.VendorCategoryId)
              .split(',')
              .map((s) => parseInt(s.trim(), 10))
              .filter((n) => !Number.isNaN(n))
        : [];
    if (raw && typeof raw === 'object' && raw !== null && !Array.isArray(raw)) {
        return raw;
    }
    const nums = Array.isArray(raw)
        ? raw.map((x) => parseInt(x, 10)).filter((n) => !Number.isNaN(n))
        : String(raw || '')
              .split(',')
              .map((x) => parseInt(x.trim(), 10))
              .filter((n) => !Number.isNaN(n));
    if (catIds.length === 1 && nums.length > 0) {
        return { [catIds[0]]: nums };
    }
    if (catIds.length > 0) {
        return Object.fromEntries(catIds.map((id) => [id, []]));
    }
    return {};
};

const Footer = () => {
    const { progress, onBack } = useWebstoreSetupWizard();

    return (
        <VendorAccountSetupStepFooter
            progress={progress}
            handleOnBack={() => onBack()}
            showBack
            pearlConnectFooter
        />
    );
};

const DetailsForm = ({ setNameErrorRef }) => {
    const { t } = useTranslation();
    const { webstore } = useWebstoreContext();
    const {
        appData: { categoryList },
    } = useStore();
    const { setValue, watch, setError } = useFormContext();
    const categoryId = watch('VendorCategoryId');
    const crawledPrefillApplied = useRef(false);

    useEffect(() => {
        if (setNameErrorRef) {
            setNameErrorRef.current = (msg) =>
                setError('WebstoreName', { type: 'manual', message: msg });
        }
    }, [setError, setNameErrorRef]);

    const categoryListItems = useMemo(
        () =>
            categoryList.map((x) => ({
                id: x.id,
                label: x.name,
                imageUrl: x.image?.url,
                subCategories: x.subCategories.map((y) => ({
                    id: y.id,
                    label: y.name,
                    imageUrl: null,
                    categoryId: x.id,
                })),
            })),
        [categoryList]
    );

    const [selectedCategoryIds, setSelectedCategoryIds] = useState(
        webstore.VendorCategoryId
            ? webstore.VendorCategoryId.split(',').map((item) => parseFloat(item.trim()))
            : []
    );
    const [selectedCategoryDetail, setSelectedCategoryDetail] = useState(
        categoryListItems?.filter((item) => selectedCategoryIds?.includes(item.id)) || []
    );

    useEffect(() => {
        if (categoryId) {
            const categoryIdsArray = categoryId.split(',').map((id) => parseInt(id.trim(), 10));
            if (!categoryIdsArray.includes(2)) {
                setValue('Capacity', null);
            }
        }
    }, [categoryId, setValue]);

    useEffect(() => {
        if (
            crawledPrefillApplied.current ||
            webstore.VendorCategoryId ||
            webstore?.CrawledData?.category == null ||
            webstore?.CrawledData?.category === ''
        ) {
            return;
        }
        const idStr = String(webstore.CrawledData.category).split(',')[0].trim();
        const numId = parseInt(idStr, 10);
        if (Number.isNaN(numId)) {
            return;
        }
        crawledPrefillApplied.current = true;
        setValue('VendorCategoryId', idStr);
        setValue('VendorSubCategoryIds', { [numId]: [] });
        setSelectedCategoryIds([numId]);
        setSelectedCategoryDetail(categoryListItems.filter((item) => item.id === numId));
    }, [webstore.VendorCategoryId, webstore.CrawledData, categoryListItems, setValue]);

    const [CrawledVendorAddress, setCrawledVendorAddress] = useState({
        addressLine: '',
        city: '',
        state: '',
        zipcode: '',
    });

    useEffect(() => {
        const address = webstore?.CrawledData?.address || '';
        const parts = address.split(',').map((part) => part.trim());

        if (Array.isArray(parts) && parts.length >= 2) {
            const stateZip = parts[parts.length - 2] || '';
            const city = parts[parts.length - 3] || '';
            const addressLines = parts.slice(0, parts.length - 3).join(', ');

            const [state, zipcode] = (stateZip || '').trim().split(/\s+/);

            setCrawledVendorAddress({
                addressLine: addressLines || '',
                city: city || 'New York',
                state: state || 'NY',
                zipcode: zipcode || '10001',
            });
        }
    }, [webstore]);
    const fields = () => {
        return webstore.Plan === 'planthree' && webstore.WebstoreName === null
            ? [
                  <div key={0}>
                      <TextInput
                          formFieldName="WebstoreName"
                          placeholder={t(`Forms.${formName}.WebstoreName`)}
                      />
                      <PearlError name="WebstoreName" />
                  </div>,
                  <div key={1} style={{ position: 'relative' }}>
                      <TextInput
                          formFieldName="EINNumber"
                          placeholder={t(`Forms.${formName}.EINNumberLabel`)}
                      />
                      <div className="tooltipEINNumber">
                          <TooltipComponent
                              showCloseButton={false}
                              header={''}
                              width={'291'}
                              height={'95'}
                              bottomSmall={'148'}
                              leftSmall={'-610'}
                              arrowBottomSmallscreen={'-21'}
                              arrowLeftSmallscreen={'91'}
                              arrowBottom={'-22'}
                              arrowLeft={'90'}
                              bottom={'148'}
                              left={'-605'}
                              content={t(`Forms.${formName}.EINNumberTooltip`)}
                          />
                      </div>
                      <PearlError name="EINNumber" />
                  </div>,
                  <div key={2}>
                      <SocialLinksSection />
                  </div>,
              ]
            : [
                  <div key={0}>
                      <TextInput
                          formFieldName="WebstoreName"
                          placeholder={t(`Forms.${formName}.WebstoreName`)}
                      />
                      <PearlError name="WebstoreName" />
                  </div>,
                  <div key={1}>
                      <SocialLinksSection crawledVendorWebsite={webstore?.CrawledData?.website} />
                  </div>,
              ];
    };
    return (
        <div className="formComponent detailsContainer">
            <div>
                <p
                    className="label"
                    style={{
                        fontFamily: 'Objektiv Mk2 Bold',
                        fontSize: '12px',
                        lineHeight: '18px',
                    }}
                >
                    {t(`Forms.${formName}.InfoTitle`)}
                </p>
                <FormRow columns={fields()} className="business-input-wrapper" />
            </div>
            <p
                className="label"
                style={{
                    fontFamily: 'Objektiv Mk2 Bold',
                    fontSize: '12px',
                    lineHeight: '18px',
                    marginTop: '24px',
                }}
            >
                {t(`Forms.${formName}.VendorCategoryIdLabel`)}
            </p>
            <FormRow
                className="business-input-wrapper"
                columns={[
                    <div key={0}>
                        <VendorCategoryMultiSelect
                            categoryListItems={categoryListItems}
                            selectedCategoryIds={selectedCategoryIds}
                            setSelectedCategoryIds={setSelectedCategoryIds}
                            selectedCategoryDetail={selectedCategoryDetail}
                            setSelectedCategoryDetail={setSelectedCategoryDetail}
                        />
                        <PearlError name="VendorCategoryId" />
                    </div>,
                ]}
            />
            {selectedCategoryDetail?.length > 0 && (
                <VendorSubcategoryMultiSelect selectedCategoryDetail={selectedCategoryDetail} />
            )}
            <p
                className="label"
                style={{
                    fontFamily: 'Objektiv Mk2 Bold',
                    fontSize: '12px',
                    lineHeight: '18px',
                    marginTop: '24px',
                }}
            >
                {t(`Forms.${formName}.AddressTitle`)}
            </p>
            <AddressAutoComplete
                addressLine1Label={t(`Forms.${formName}.AddressLine1`)}
                cityLabel={t(`Forms.${formName}.AddressCity`)}
                stateLabel={t(`Forms.${formName}.AddressState`)}
                zipcodeLabel={t(`Forms.${formName}.AddressZipcode`)}
                crawledVendorAddress={CrawledVendorAddress}
            ></AddressAutoComplete>
        </div>
    );
};

const VendorBusinessDetailsFormNew = () => {
    const { getBusinessAddress, createNewBusinessAddress } = WebstoreAddressService;
    const { link, view } = useTealium();
    const { vendor } = useVendorContext();
    const { webstore } = useWebstoreContext();
    const {
        appData: { categoryList },
    } = useStore();
    const { onSubmit, currentStepCount, totalSteps } = useWebstoreSetupWizard();

    const { displayCurrentStep, displayTotalSteps } = getSetupStepDisplayCounts(
        currentStepCount,
        totalSteps
    );

    const { t } = useTranslation();
    const { track, events, renewalFrequencies, getProjectedRenewalDate, toVendorId } =
        useAnalytics();

    useEffect(() => {
        const stepName = t(`Forms.${formName}.StepName`);
        const tealiumData = buildWebstoreSetupStepPageData(
            stepName,
            webstore,
            currentStepCount,
            totalSteps
        );
        view(tealiumData);
    }, []);

    // PLAN_TIER_SELECTED fires here, not at the plan-pick step -- this is
    // the wizard's last step, reached after payment for paid tiers, so the
    // billing period is a confirmed fact rather than a guess made before
    // the vendor had even chosen it.
    //
    // Two sources, in order of trustworthiness:
    //   1. subscription.priceInterval -- server-hydrated, read live from
    //      Stripe (see the API's withLiveStripeSubscriptionData). Survives a
    //      page reload, which is why it's preferred.
    //   2. stripeData.priceId -- written client-side only, by
    //      VendorPlanForm.js (free tier) / VendorPayment.js (paid), and
    //      never returned by the API, so a reload wipes it. Kept as a
    //      fallback for the no-reload path, where the subscription object
    //      may not have been refreshed yet.
    // If neither resolves, both properties are omitted rather than guessed.
    //
    // The localStorage guard keeps a remount (a reload on this step, or
    // navigating back and forward) from firing a duplicate -- this is meant
    // to be one event per vendor per signup, not one per mount.
    useEffect(() => {
        const storageService = StorageService();
        const alreadySentKey = `planTierSelectedSent_${vendor?.id}`;
        if (!vendor?.id || storageService.get(alreadySentKey)) {
            return;
        }

        const localStripeData = storageService.get('stripeData');
        const stripeData = localStripeData ? JSON.parse(localStripeData) : [];
        const matchingPrice = stripeData.find(
            (item) => item.stripePriceId === vendor?.stripeData?.priceId
        );
        const billingPeriod =
            vendor?.stripeData?.subscription?.priceInterval || matchingPrice?.billingFrequency;

        track(events.PLAN_TIER_SELECTED, {
            vendor_id: toVendorId(vendor.id),
            plan_tier: webstore.SelectedPlan,
            source: 'signup',
            ...(billingPeriod
                ? {
                      renewal_frequency:
                          billingPeriod === 'year'
                              ? renewalFrequencies.ANNUALLY
                              : renewalFrequencies.MONTHLY,
                      projected_renewal_date: getProjectedRenewalDate(billingPeriod),
                  }
                : {}),
        });

        storageService.set(alreadySentKey, 'true');
    }, []);

    const businessAddress = getBusinessAddress(webstore);
    const vendorBusinessDetailsFormSchema = vendorBusinessDetailsFormSchemaWrapper(categoryList);
    const schemas = [vendorBusinessDetailsFormSchema, vendorBusinessAddressFormSchema];

    const mergedSchemas = mergeSchemas(schemas);

    const formDefaultValues = {
        WebstoreName: webstore.WebstoreName,
        VendorCategoryId: webstore.VendorCategoryId,
        VendorSubCategoryIds: buildDefaultVendorSubCategoryIds(webstore),
        [formFieldNames.ServiceAddressStreet]: businessAddress?.address1,
        [formFieldNames.ServiceAddressCity]: businessAddress?.city,
        [formFieldNames.ServiceAddressState]: businessAddress?.state,
        [formFieldNames.ServiceAddressZipcode]: businessAddress?.zipCode,
        Website: webstore.Website,
        [formFieldNames.DisplayStoreAddress]: false,
        [formFieldNames.IsServiceTypeSelected]: true,
        [formFieldNames.ServiceAddressIsBusinessAddress]: false,
        [formFieldNames.PhysicalStore]: false,
        [formFieldNames.Travels]: true,
        [formFieldNames.TravelOption]: 'locally',
    };

    const formOptions = {
        mode: 'onChange',
        defaultValues: webstore?.ActiveFeatures?.feature_premiumBadge
            ? { ...formDefaultValues, EINNumber: '' }
            : formDefaultValues,
    };
    const handleAddress = async (data) => {
        const updatedAddress = {
            address1: data[formFieldNames.ServiceAddressStreet],
            city: data[formFieldNames.ServiceAddressCity],
            state: data[formFieldNames.ServiceAddressState],
            zipCode: data[formFieldNames.ServiceAddressZipcode],
            addressType: 'business',
        };

        let businessAddress = getBusinessAddress(webstore);

        if (businessAddress) {
            Object.keys(updatedAddress).forEach((key) => {
                businessAddress[key] = updatedAddress[key];
            });
        } else {
            businessAddress = {
                ...createNewBusinessAddress(),
                ...updatedAddress,
            };
            webstore.Addresses.push(businessAddress);
        }
    };
    const setNameErrorRef = useRef(null);
    const { openModal, closeModal } = usePearlModal();
    const claimableWebstoresService = useMemo(() => ClaimableWebstoresService(), []);
    const geocodeService = useMemo(() => GeocodeService(), []);

    const proceedToSubmit = (data) => {
        const tealiumData = {
            event_name: 'setup-step-advance-click',
            setup_step_number: 1,
            setup_step_total_step_count: totalSteps,
            setup_step_name: t(`Forms.${formName}.StepName`),
            setup_step_form_data: data,
            setup_step_patch_data: data,
            webstore_id: webstore.Id,
        };
        link(tealiumData);
        onSubmit(data);
    };

    const openClaimableDialog = (results, data) => {
        openModal(
            <ClaimableWebstoresDialog
                results={results}
                onContinue={() => {
                    closeModal();
                    proceedToSubmit(data);
                }}
            />,
            PearlModalSizes.VERIFY_EMAIL,
            '',
            null,
            true,
            '24px',
            8,
            true,
            0.45,
            8,
            'stretch',
            '6px'
        );
    };

    const resolveLatLon = async (data) => {
        const city = data[formFieldNames.ServiceAddressCity];
        const state = data[formFieldNames.ServiceAddressState];
        if (!city || !state) return {};
        try {
            const geo = await geocodeService.getGeocodeByAddress(city, state);
            const lat = parseFloat(geo?.latitude);
            const lon = parseFloat(geo?.longitude);
            if (Number.isFinite(lat) && Number.isFinite(lon)) {
                return { latitude: lat, longitude: lon };
            }
        } catch (error) {
            console.warn('geocode for claimable search failed', error);
        }
        return {};
    };

    const checkClaimableWebstores = async (data) => {
        try {
            const { latitude, longitude } = await resolveLatLon(data);
            const stateCode = data[formFieldNames.ServiceAddressState];
            const res = await claimableWebstoresService.searchClaimableWebstores({
                name: data.WebstoreName,
                categoryName: resolveCategoryName(categoryList, data.VendorCategoryId),
                subcategoryName: resolveSubcategoryName(
                    categoryList,
                    data.VendorSubCategoryIds,
                    data.VendorCategoryId
                ),
                stateCode,
                latitude,
                longitude,
            });
            return res?.data?.results || [];
        } catch (error) {
            console.warn('claimable-webstores-search failed; proceeding with submit', error);
            return [];
        }
    };

    const handleSubmit = async (data) => {
        if (data.WebstoreName) {
            data.WebstoreName = data.WebstoreName.trim();
            const wsApi = WebstoreService();
            const res = await wsApi.getCheckBusinessNameAvailable(data.WebstoreName, webstore.Id);
            if (!res.ok || !res.data.nameIsAvailable) {
                setNameErrorRef.current?.(t('Forms.Validation.BusinessNameExists'));
                return;
            }
        }
        const categoryIdsArray = data.VendorCategoryId
            ? data.VendorCategoryId.split(',').map((id) => parseInt(id.trim(), 10))
            : [];
        if (!categoryIdsArray.includes(2)) {
            data.Capacity = null;
        }
        await handleAddress(data);
        if (data.EINNumber) {
            try {
                await equifaxService().verifyBusiness({ ...data, webstore_id: webstore.Id });
            } catch (error) {
                console.log(error);
            }
        }
        if (webstore?.CrawledData) {
            try {
                await equifaxService().verifyBusiness({
                    crawledVendorId: webstore?.CrawledData.id,
                    webstore_id: webstore.Id,
                    copyData: true,
                });
            } catch (error) {
                console.log(error);
            }
        }

        const claimableResults = await checkClaimableWebstores(data);
        if (claimableResults.length > 0) {
            openClaimableDialog(claimableResults, data);
            return;
        }

        proceedToSubmit(data);
    };

    return (
        <PearlForm
            StyledForm={VendorBusinessDetailsFormStyledNew}
            formName={formName}
            formOptions={formOptions}
            validationSchema={mergedSchemas}
            onSubmit={handleSubmit}
        >
            <VendorAccountSetupStepLayout
                header={
                    <NewVendorWebstoreSetupHeader
                        stepName={formName}
                        currentStepCount={displayCurrentStep}
                        totalSteps={displayTotalSteps}
                        showMobileTopBar
                        pearlConnectStepTheme
                        pearlConnectSubtitleAccent
                    />
                }
                formComponent={
                    <DetailsForm
                        vendor={vendor}
                        webstore={webstore}
                        setNameErrorRef={setNameErrorRef}
                    />
                }
                footer={<Footer />}
            />
        </PearlForm>
    );
};

export { VendorBusinessDetailsFormNew };
